import React, { useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { ModalConfirm } from '../../../../components';
import { Content, Reason } from '../../../../components/feedback-form-content';
import { i18n } from '../../../../helpers/i18n';
import { useActions } from '../../../../hooks';
import { selectIsFeedbackFormVisible } from '../../selectors';
import { setFeedbackComponentVisibility, submitFeedback } from '../../slice';
import { Stars } from '../stars';
import styles from './styles.css';

export const FeedbackModal = () => {
    const isVisible = useSelector(selectIsFeedbackFormVisible);

    const [rating, setRating] = useState<number>();
    const [message, setMessage] = useState<string>('');

    const { checkedReasons, addReason, deleteReason } = useMemo(() => {
        const checkedReasons = new Set<Reason>();

        return {
            checkedReasons,
            addReason: (reason: Reason) => checkedReasons.add(reason),
            deleteReason: (reason: Reason) => checkedReasons.delete(reason),
        };
    }, []);

    const onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const actions = useActions({ setFeedbackComponentVisibility, submitFeedback });

    const onSubmit = () => {
        const additionalData = rating && rating < 3 ? { comment: message, reasons: [...checkedReasons] } : {};

        actions.submitFeedback({ rating, ...additionalData });
        onClose();
    };

    const onClose = () => {
        setRating(undefined);
        setMessage('');
        checkedReasons.clear();

        actions.setFeedbackComponentVisibility(false);
    };

    if (!isVisible) {
        return null;
    }

    const showContent = Boolean(rating && rating < 4);

    return (
        <ModalConfirm
            onClose={onClose}
            onConfirm={onSubmit}
            headerText={i18n('feedback', 'feedback-form-header')}
            confirmButtonText={i18n('feedback', 'submit')}
            testId="feedback-modal"
            scrollContent={showContent}
        >
            <div className={styles.container}>
                <Stars onRatingChange={setRating} />
                {showContent && (
                    <Content
                        message={message}
                        onMessageChange={onMessageChange}
                        checkedReasons={checkedReasons}
                        addReason={addReason}
                        deleteReason={deleteReason}
                    />
                )}
            </div>
        </ModalConfirm>
    );
};
